import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { fetchTableData } from "../../services/sadtService";

// Status label → colour map (mirrors CSS semantic tokens)
const STATUS_STYLE = {
  "Overload":         { color: "#B0532D", background: "rgba(176,83,45,0.10)" },
  "Underload":        { color: "#7A6B3F", background: "rgba(122,107,63,0.10)" },
  "Perfectly Loaded": { color: "#4A6B5A", background: "rgba(74,107,90,0.10)"  },
};

// ── Shared helpers ────────────────────────────────────────────────────
function fmt(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n % 1 === 0 ? n.toLocaleString() : n.toFixed(1);
}

function StatusBadge({ value }) {
  const s = STATUS_STYLE[value] || {};
  return (
    <span
      className="inline-block px-2 py-0.5"
      style={{ fontSize: "9px", fontWeight: 500, fontFamily: "var(--font-sans)", ...s }}
    >
      {value || "—"}
    </span>
  );
}

// ── Metric box — uses gap-px parent to avoid double borders ───────────
function MetricBox({ label, value, color }) {
  return (
    <div className="bg-[var(--color-paper)] px-5 py-5">
      <div
        className="numeric"
        style={{ fontSize: "2.125rem", lineHeight: 1.1, fontWeight: 400, color: color || "var(--color-ink)" }}
      >
        {value ?? "—"}
      </div>
      <div
        className="mt-2.5"
        style={{
          fontSize: "9px", fontWeight: 500, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "var(--color-ink-3)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── Widget shell ──────────────────────────────────────────────────────
function Widget({ eyebrow, title, tableName, rows, loading, emptyText, maxH = "22rem", children }) {
  const body = loading
    ? <div className="px-5 py-8" style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>Loading…</div>
    : rows == null
      ? <div className="px-5 py-10 text-center" style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>
          Calculator output not yet available — run calculators after importing data.
        </div>
      : rows.length === 0
        ? <div className="px-5 py-10 text-center" style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>
            {emptyText || "No rows."}
          </div>
        : children;

  return (
    <div className="border border-[var(--color-rule)] flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--color-rule)] flex items-center justify-between gap-4 shrink-0">
        <div>
          <div className="eyebrow" style={{ fontSize: "9px" }}>{eyebrow}</div>
          <h3 className="display mt-1.5" style={{ fontSize: "var(--text-xl)" }}>{title}</h3>
        </div>
        {tableName && (
          <Link
            to={`/app/data/${encodeURIComponent(tableName)}`}
            className="shrink-0 inline-flex items-center gap-1 hover:text-[var(--color-ink)] transition-colors"
            style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
          >
            Full data <ExternalLink size={9} />
          </Link>
        )}
      </div>
      <div className="overflow-auto" style={{ maxHeight: maxH }}>{body}</div>
    </div>
  );
}

// ── Mini-table ────────────────────────────────────────────────────────
function MiniTable({ cols, rows, renderCell }) {
  return (
    <table
      className="w-full border-collapse"
      style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}
    >
      <thead>
        <tr>
          {cols.map((c) => (
            <th
              key={c}
              className="px-4 py-2.5 text-left border-b border-[var(--color-rule)] whitespace-nowrap font-medium"
              style={{
                fontSize: "9px", letterSpacing: "0.13em", textTransform: "uppercase",
                color: "var(--color-ink-3)", fontFamily: "var(--font-sans)",
                background: "var(--color-paper-2)",
              }}
            >
              {c.replace(/_/g, " ")}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 !== 0 ? "bg-[var(--color-paper-2)]" : ""}>
            {cols.map((c) => (
              <td key={c} className="px-4 py-2 border-b border-[var(--color-rule)] whitespace-nowrap">
                {renderCell ? renderCell(c, row[c], row) : fmt(row[c])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const entries = [
      ["load",     "sadt_calculated_load"],
      ["overload", "sadt_overload_summary"],
      ["risk",     "sadt_single_instructor_risk"],
      ["nonteach", "sadt_non_teaching_load"],
      ["term",     "sadt_term_distribution_summary"],
      ["cch",      "sadt_cch_by_status"],
    ];

    Promise.allSettled(entries.map(([, t]) => fetchTableData(t, { limit: 1000 })))
      .then((results) => {
        const d = {};
        entries.forEach(([key], i) => {
          if (results[i].status === "fulfilled") d[key] = results[i].value.rows ?? [];
        });
        setData(d);
        setLoading(false);
      });
  }, []);

  // ── Derived metrics ───────────────────────────────────────────────
  const loadRows    = data.load ?? null;
  const overloaded  = loadRows ? loadRows.filter((r) => r.Status === "Overload").length         : null;
  const perfectly   = loadRows ? loadRows.filter((r) => r.Status === "Perfectly Loaded").length  : null;
  const underloaded = loadRows ? loadRows.filter((r) => r.Status === "Underload").length         : null;
  const highRiskCnt = data.risk ? data.risk.filter((r) => r.Risk_Level === "High Risk").length   : null;

  const total = loadRows?.length ?? 0;
  const barSegments = total > 0
    ? [
        { label: "Overload",         pct: (overloaded  / total) * 100, color: "var(--color-status-over)"  },
        { label: "Perfectly Loaded", pct: (perfectly   / total) * 100, color: "var(--color-status-even)"  },
        { label: "Underload",        pct: (underloaded / total) * 100, color: "var(--color-status-under)" },
      ]
    : [];

  const highRiskRows = data.risk
    ? data.risk.filter((r) => r.Risk_Level === "High Risk").slice(0, 20)
    : null;

  return (
    <div className="px-8 lg:px-12 pt-10 pb-16">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="reveal">
        <div className="eyebrow">DASHBOARD · LOAD OVERVIEW</div>
      </div>
      <h1
        className="reveal display mt-4"
        style={{ fontSize: "var(--text-3xl)", animationDelay: "120ms" }}
      >
        Load overview
      </h1>

      {/* ── Distribution bar ─────────────────────────────────────────── */}
      {!loading && barSegments.length > 0 && (
        <div className="reveal mt-6" style={{ animationDelay: "180ms" }}>
          <div
            className="flex overflow-hidden"
            style={{ height: 10, borderRadius: 2, background: "var(--color-rule)" }}
          >
            {barSegments.map((seg) => (
              <div
                key={seg.label}
                style={{
                  width: `${seg.pct}%`,
                  background: seg.color,
                  transition: "width 800ms var(--ease-editorial)",
                }}
                title={`${seg.label}: ${Math.round(seg.pct)}%`}
              />
            ))}
          </div>
          <div className="flex items-center gap-6 mt-2.5">
            {barSegments.map((seg) => (
              <div key={seg.label} className="flex items-center gap-1.5">
                <div style={{ width: 8, height: 8, background: seg.color, borderRadius: 1 }} />
                <span style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-ink-3)", fontFamily: "var(--font-sans)" }}>
                  {seg.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Metric boxes ─────────────────────────────────────────────── */}
      <div
        className="reveal grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--color-rule)] border border-[var(--color-rule)] mt-8 mb-10"
        style={{ animationDelay: "240ms" }}
      >
        <MetricBox label="Overloaded"         value={overloaded}  color="var(--color-status-over)"   />
        <MetricBox label="Perfectly loaded"   value={perfectly}   color="var(--color-status-even)"   />
        <MetricBox label="Underloaded"        value={underloaded} color="var(--color-status-under)"  />
        <MetricBox label="High-risk courses"  value={highRiskCnt} color="var(--color-accent)"        />
      </div>

      {/* ── Overload breakdown — full width ──────────────────────────── */}
      <div className="reveal mb-6" style={{ animationDelay: "300ms" }}>
        <Widget
          eyebrow="OVERLOAD · SUMMARY"
          title="By academic coordinator and status"
          tableName="sadt_overload_summary"
          loading={loading}
          rows={data.overload ?? null}
        >
          <MiniTable
            cols={["Reporting_AC", "Status", "Count", "Total_Assigned_CCH", "Total_Target_CCH"]}
            rows={data.overload ?? []}
            renderCell={(c, v) => c === "Status" ? <StatusBadge value={v} /> : fmt(v)}
          />
        </Widget>
      </div>

      {/* ── 2-col: High-risk | Non-teaching ──────────────────────────── */}
      <div
        className="reveal grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
        style={{ animationDelay: "360ms" }}
      >
        <Widget
          eyebrow="SINGLE INSTRUCTOR · RISK"
          title="High-risk courses"
          tableName="sadt_single_instructor_risk"
          loading={loading}
          rows={highRiskRows}
          emptyText="No high-risk courses — all active courses have multiple instructors."
        >
          <MiniTable
            cols={["Subj", "Crse", "Reporting_AC", "Active_Sections"]}
            rows={highRiskRows ?? []}
          />
        </Widget>

        <Widget
          eyebrow="NON-TEACHING · LOAD"
          title="By instructor"
          tableName="sadt_non_teaching_load"
          loading={loading}
          rows={data.nonteach ?? null}
        >
          <MiniTable
            cols={["Instructor_Name", "Reporting_AC", "Non_Teaching_CCH", "Non_Teaching_Pct"]}
            rows={data.nonteach ?? []}
          />
        </Widget>
      </div>

      {/* ── 2-col: Term distribution | CCH by status ─────────────────── */}
      <div
        className="reveal grid grid-cols-1 lg:grid-cols-2 gap-6"
        style={{ animationDelay: "420ms" }}
      >
        <Widget
          eyebrow="TERM · DISTRIBUTION"
          title="CCH by term and AC"
          tableName="sadt_term_distribution_summary"
          loading={loading}
          rows={data.term ?? null}
        >
          <MiniTable
            cols={["Reporting_AC", "Term_Name", "Total_CCH", "Pct_of_AC_Total"]}
            rows={data.term ?? []}
          />
        </Widget>

        <Widget
          eyebrow="CCH · BY SECTION STATUS"
          title="Coverage breakdown"
          tableName="sadt_cch_by_status"
          loading={loading}
          rows={data.cch ?? null}
        >
          <MiniTable
            cols={["Reporting_AC", "SectionStatus", "Effective_CCH", "Pct_of_AC_Total"]}
            rows={(data.cch ?? []).slice(0, 40)}
          />
        </Widget>
      </div>
    </div>
  );
}
